import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Collapse,
  TextField,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import { RiSettings3Line } from "@remixicon/react";
import { ComplianceIssueBooleanIcon } from "./UI/ComplianceIssueBooleanIcon";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import JsonDisplayModalWithFetch from "./UI/JsonDisplayModalWithFetch";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import useApi from "../../hooks/useGaneshApi";
import { useQuery } from "@tanstack/react-query";
import { useError } from "../../contexts/ErrorContext";
import KpiCard from "../UI/KpiCard";
import SummaryCard from "../UI/SummaryCard";
import {
  formatDateYYYYMMDD,
  formatFileSize,
  formatNumber,
} from "utils/formatting";
import { endOfWeek, startOfWeek, subDays } from "date-fns";
import { useComplianceTrends } from "../../hooks/useComplianceTrends";
import { useComplianceThresholds } from "../../hooks/useComplianceThresholds";
import {
  useComplianceFiltering,
  FilterDefinition,
} from "../../hooks/useComplianceFiltering";
import { StructureStats_T } from "../../types/Compliance";
import { RiExternalLinkLine } from "@remixicon/react";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";

type ActiveFilter_T =
  | "rowCount"
  | "fileSize"
  | "compliant"
  | "columnCount"
  | "sameType"
  | "anyIssue";

interface StructureChecksProps {
  dateStart?: string;
  dateEnd?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: { items: [] },
  },
  density: "compact",
  columns: {
    columnVisibilityModel: {
      current_file_size_kb: false,
      previous_file_size_kb: false,
      current_row_count: false,
      previous_row_count: false,
      current_column_count: false,
      previous_column_count: false,
    },
  },
  sorting: { sortModel: [{ field: "delivery_name", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
};

// Threshold definitions are now centralized in the useComplianceThresholds hook

const StructureChecks: React.FC<StructureChecksProps> = ({
  dateStart: propDateStart,
  dateEnd: propDateEnd,
}) => {
  const { apiRequest } = useApi();
  const { showError } = useError();

  // Get today's date for default date range
  const today = new Date();

  // Date range state - use props when available, fall back to current week
  const [dateStart, setDateStart] = useState(
    propDateStart || formatDateYYYYMMDD(startOfWeek(today, { weekStartsOn: 1 }))
  );
  const [dateEnd, setDateEnd] = useState(
    propDateEnd || formatDateYYYYMMDD(endOfWeek(today, { weekStartsOn: 0 }))
  );

  // Update internal dates when props change
  useEffect(() => {
    if (propDateStart) setDateStart(propDateStart);
  }, [propDateStart]);

  useEffect(() => {
    if (propDateEnd) setDateEnd(propDateEnd);
  }, [propDateEnd]);

  // Use the compliance thresholds hook (no persistence - always uses team defaults)
  const thresholdConfig = useComplianceThresholds({
    type: "structure",
  });

  // Get shipping data to calculate fulfillments scheduled total
  const { overviewData: shippingOverviewData } = useShippingDataCoordinator(
    dateStart,
    dateEnd
  );

  const fetchStructureStats = async (): Promise<StructureStats_T[]> => {
    const response = await apiRequest<StructureStats_T[]>(
      `/v1/deliveries/structure_stats?dateStart=${dateStart}&dateEnd=${dateEnd}`,
      { method: "GET" }
    );
    return response.data || [];
  };

  const fetchPreviousWeekStats = async (): Promise<StructureStats_T[]> => {
    // Calculate previous week dates
    const currentStartDate = new Date(dateStart);
    const currentEndDate = new Date(dateEnd);

    const previousStart = formatDateYYYYMMDD(subDays(currentStartDate, 7));
    const previousEnd = formatDateYYYYMMDD(subDays(currentEndDate, 7));

    const response = await apiRequest<StructureStats_T[]>(
      `/v1/deliveries/structure_stats?dateStart=${previousStart}&dateEnd=${previousEnd}`,
      { method: "GET" }
    );
    return response.data || [];
  };

  const {
    data: structureStatsData,
    isLoading,
    error,
  } = useQuery<StructureStats_T[], Error>({
    queryKey: ["structureStats", dateStart, dateEnd],
    queryFn: fetchStructureStats,
    staleTime: 120000, // 2 minutes
    refetchInterval: false,
  });

  const {
    data: previousWeekData,
    isLoading: isLoadingPrevious,
    error: previousWeekError,
  } = useQuery<StructureStats_T[], Error>({
    queryKey: ["previousWeekStats", dateStart, dateEnd],
    queryFn: fetchPreviousWeekStats,
    staleTime: 120000, // 2 minutes
    refetchInterval: false,
    enabled: !!dateStart && !!dateEnd,
  });

  useEffect(() => {
    if (error) {
      showError(error.message);
    }
    if (previousWeekError) {
      showError(previousWeekError.message);
    }
  }, [error, previousWeekError]);

  // Define a single helper function for compliance checking
  const isCompliant = (d: StructureStats_T): boolean =>
    (d.row_count_pct_diff === null ||
      Math.abs(d.row_count_pct_diff) <= thresholdConfig.thresholds.rowCount) &&
    (d.file_size_pct_diff === null ||
      Math.abs(d.file_size_pct_diff) <= thresholdConfig.thresholds.fileSize) &&
    (d.column_count_diff === 0 || d.column_count_diff === null) &&
    (d.same_type === true || d.same_type === null);

  // Define filter definitions for structure compliance
  const filterDefinitions: Record<
    ActiveFilter_T,
    FilterDefinition<StructureStats_T>
  > = {
    rowCount: {
      key: "rowCount",
      label: "Row Count Issues",
      description: `Row Count Issues (outside ±${
        thresholdConfig.thresholds.rowCount?.toFixed(1) || "2.0"
      }%)`,
      filter: (data) =>
        data.filter(
          (d) =>
            Math.abs(d.row_count_pct_diff) > thresholdConfig.thresholds.rowCount
        ),
    },
    fileSize: {
      key: "fileSize",
      label: "File Size Issues",
      description: `File Size Issues (outside ±${
        thresholdConfig.thresholds.fileSize?.toFixed(1) || "5.0"
      }%)`,
      filter: (data) =>
        data.filter(
          (d) =>
            Math.abs(d.file_size_pct_diff) >
              thresholdConfig.thresholds.fileSize ||
            d.current_file_size_kb === 0 ||
            d.current_file_size_kb < 0.1
        ),
    },
    columnCount: {
      key: "columnCount",
      label: "Column Count Issues",
      description: "Column Count Issues (any difference from 0)",
      filter: (data) =>
        data.filter(
          (d) => d.column_count_diff !== 0 && d.column_count_diff !== null
        ),
    },
    sameType: {
      key: "typeMismatch",
      label: "File Type Issues",
      description: "File Type Issues (type mismatch detected)",
      filter: (data) => data.filter((d) => d.same_type === false),
    },
    anyIssue: {
      key: "anyIssue",
      label: "Deliveries with Issues",
      description: "Deliveries with Any Issue (one or more issues detected)",
      filter: (data) => data.filter((d) => !isCompliant(d)),
    },
    compliant: {
      key: "compliant",
      label: "Compliant Deliveries",
      description: "Compliant Deliveries (within all thresholds)",
      filter: (data) => data.filter((d) => isCompliant(d)),
    },
  };

  // Transform data to add computed columns for table display
  const transformDataForDisplay = (delivery: StructureStats_T) => ({
    ...delivery,
    file_size_issue:
      Math.abs(delivery.file_size_pct_diff) >=
      thresholdConfig.thresholds.fileSize,
    row_count_issue:
      Math.abs(delivery.row_count_pct_diff) >=
      thresholdConfig.thresholds.rowCount,
    column_count_issue:
      delivery.column_count_diff !== 0 && delivery.column_count_diff !== null,
    type_mismatch_issue: delivery.same_type === false,
  });

  // Use the compliance filtering hook
  const filterConfig = useComplianceFiltering({
    data: structureStatsData || [],
    filters: filterDefinitions,
    transformDataForDisplay,
  });

  // Calculate counts (for summary cards)
  const totalDeliveries = structureStatsData?.length || 0;
  const deliveriesWithIssuesCount = filterDefinitions.anyIssue.filter(
    structureStatsData || []
  ).length;
  const rowCountFailuresCount = filterDefinitions.rowCount.filter(
    structureStatsData || []
  ).length;
  const fileSizeFailuresCount = filterDefinitions.fileSize.filter(
    structureStatsData || []
  ).length;
  const columnCountFailuresCount = filterDefinitions.columnCount.filter(
    structureStatsData || []
  ).length;
  const sameTypeFailuresCount = filterDefinitions.sameType.filter(
    structureStatsData || []
  ).length;

  const getMetric = (data: StructureStats_T[]) => {
    const compliant = filterDefinitions.compliant.filter(data);
    return data.length > 0 ? (compliant.length / data.length) * 100 : 0;
  };

  // Use trends hook for compliance rate
  const complianceMetrics = useComplianceTrends(
    {
      current: structureStatsData || [],
      previous: previousWeekData || [],
      isLoading,
      isLoadingPrevious,
    },
    getMetric
  );

  // Extract compliance rate metrics
  const complianceRate = complianceMetrics.currentValue;
  const previousComplianceRate = complianceMetrics.previousValue;
  const complianceTrend = complianceMetrics.trend;

  // Calculate fulfillments scheduled total from shipping data
  const fulfillmentsScheduledTotal =
    shippingOverviewData?.reduce((sum, row) => {
      return sum + (row.count_delivery_fulfillments || 0);
    }, 0) || 0;

  const columns_def: GridColDef[] = [
    { field: "delivery_id", headerName: "Delivery ID", width: 100 },
    { field: "delivery_name", headerName: "Delivery Name", width: 175 },
    {
      field: "export_id",
      headerName: "View Summary",
      width: 90,
      renderCell: (params) =>
        params.value ? (
          <JsonDisplayModalWithFetch
            fetchUrl={`/v1/exports/${params.value}/export_summary`}
            title="Export Summary"
            iconOnly={true}
            enableCollapse={true}
          />
        ) : null,
    },
    {
      field: "export_id_link",
      headerName: "Current Export ID",
      width: 100,
      valueGetter: (value, row) => row.export_id,
      renderCell: (params: GridRenderCellParams) =>
        params.row.export_id ? (
          <Link
            to={`https://kuebiko.dataplor.com/deliveries/${params.row.delivery_id}/exports/${params.row.export_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row.export_id} <RiExternalLinkLine size="14" />
          </Link>
        ) : (
          "--"
        ),
    },
    {
      field: "file_size_issue",
      headerName: "File Size Issue",
      width: 120,
      renderCell: ComplianceIssueBooleanIcon,
    },
    {
      field: "row_count_issue",
      headerName: "Row Count Issue",
      width: 120,
      renderCell: ComplianceIssueBooleanIcon,
    },
    {
      field: "column_count_issue",
      headerName: "Column Count Issue",
      width: 120,
      renderCell: ComplianceIssueBooleanIcon,
    },
    {
      field: "type_mismatch_issue",
      headerName: "Type Mismatch Issue",
      width: 120,
      renderCell: ComplianceIssueBooleanIcon,
    },
    {
      field: "current_file_size_kb",
      headerName: "Current File Size",
      width: 100,
      renderCell: (params) => formatFileSize(params.value),
    },
    {
      field: "previous_file_size_kb",
      headerName: "Previous File Size",
      width: 100,
      renderCell: (params) => formatFileSize(params.value),
    },
    { field: "file_size_pct_diff", headerName: "File Size % Diff", width: 100 },
    {
      field: "current_row_count",
      headerName: "Current Row Count",
      width: 100,
      renderCell: (params) => formatNumber(params.value),
    },
    {
      field: "previous_row_count",
      headerName: "Previous Row Count",
      width: 100,
      renderCell: (params) => formatNumber(params.value),
    },
    {
      field: "row_count_diff",
      headerName: "Row Count Diff",
      width: 100,
      renderCell: (params) => formatNumber(params.value),
    },
    { field: "row_count_pct_diff", headerName: "Row Count % Diff", width: 100 },
    {
      field: "current_column_count",
      headerName: "Current Column Count",
      width: 100,
      renderCell: (params) => formatNumber(params.value),
    },
    {
      field: "previous_column_count",
      headerName: "Previous Column Count",
      width: 100,
      renderCell: (params) => formatNumber(params.value),
    },
    { field: "column_count_diff", headerName: "Column Count Diff", width: 100 },
    { field: "file_extension", headerName: "File Extension", width: 100 },
  ];

  if (structureStatsData && structureStatsData.length === 0) {
    return (
      <Box>
        <Typography variant="h6">
          No Exported Deliveries data available yet
        </Typography>
        <Typography variant="body1" sx={{ fontStyle: "italic" }}>
          Structure compliance data is not available until export data is
          processed.
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box>
        {/* Threshold Configuration Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RiSettings3Line />}
              onClick={() =>
                thresholdConfig.setShowThresholds(
                  !thresholdConfig.showThresholds
                )
              }
              size="small"
            >
              Configure Thresholds
            </Button>
          </Box>
          <Collapse in={thresholdConfig.showThresholds}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Threshold Settings
              </Typography>
              <Grid container spacing={2} alignItems="center">
                {thresholdConfig.definitions.map((def) => (
                  <Grid item xs={12} sm={6} md={3} key={def.key}>
                    <TextField
                      label={def.label}
                      type="number"
                      value={
                        thresholdConfig.thresholds[def.key]?.toFixed(1) ||
                        def.defaultValue.toFixed(1)
                      }
                      onChange={(e) =>
                        thresholdConfig.updateThreshold(
                          def.key,
                          parseFloat(e.target.value)
                        )
                      }
                      inputProps={{
                        min: def.min,
                        max: def.max,
                        step: def.step,
                      }}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Collapse>
        </Box>

        {/* Summary Cards Section */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Grid
            container
            spacing={0}
            sx={{ ml: -4.5, maxWidth: "fit-content" }}
          >
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("none")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="Total Export Summaries"
                    value={totalDeliveries}
                    subtitle={(() => {
                      const missing = Math.max(
                        0,
                        fulfillmentsScheduledTotal - totalDeliveries
                      );
                      return missing > 0
                        ? `Missing ${missing} of ${fulfillmentsScheduledTotal} scheduled`
                        : `All ${fulfillmentsScheduledTotal} scheduled exports complete`;
                    })()}
                    metricType={
                      fulfillmentsScheduledTotal > totalDeliveries
                        ? "issues"
                        : "success"
                    }
                    clickable={totalDeliveries > 0}
                    isActive={filterConfig.activeFilter === "none"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("anyIssue")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="Deliveries with Issues"
                    value={deliveriesWithIssuesCount}
                    subtitle={
                      totalDeliveries > 0
                        ? `${(
                            (deliveriesWithIssuesCount / totalDeliveries) *
                            100
                          ).toFixed(1)}% of total`
                        : "N/A"
                    }
                    metricType="issues"
                    clickable={deliveriesWithIssuesCount > 0}
                    isActive={filterConfig.activeFilter === "anyIssue"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("rowCount")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="Row Count Issues"
                    value={rowCountFailuresCount}
                    subtitle={`Threshold: ±${thresholdConfig.thresholds.rowCount?.toFixed(
                      1
                    )}%`}
                    metricType="threshold"
                    clickable={rowCountFailuresCount > 0}
                    isActive={filterConfig.activeFilter === "rowCount"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("fileSize")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="File Size Issues"
                    value={fileSizeFailuresCount}
                    subtitle={`Threshold: ±${thresholdConfig.thresholds.fileSize?.toFixed(
                      1
                    )}%`}
                    metricType="threshold"
                    clickable={fileSizeFailuresCount > 0}
                    isActive={filterConfig.activeFilter === "fileSize"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("columnCount")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="Column Count Issues"
                    value={columnCountFailuresCount}
                    subtitle="Must match exactly"
                    metricType="requirement"
                    clickable={columnCountFailuresCount > 0}
                    isActive={filterConfig.activeFilter === "columnCount"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("sameType")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="File Type Issues"
                    value={sameTypeFailuresCount}
                    subtitle="Type mismatch detected"
                    metricType="requirement"
                    clickable={sameTypeFailuresCount > 0}
                    isActive={filterConfig.activeFilter === "sameType"}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4} lg={1.7}>
              <Box
                onClick={() => filterConfig.handleFilterChange("compliant")}
                sx={{ cursor: "pointer" }}
              >
                <Box
                  sx={{ transform: "scale(0.8)", transformOrigin: "center" }}
                >
                  <SummaryCard
                    title="Compliance Rate"
                    value={`${complianceRate.toFixed(1)}%`}
                    subtitle={`${previousComplianceRate.toFixed(
                      1
                    )}% last week (${
                      complianceTrend >= 0 ? "+" : ""
                    }${complianceTrend.toFixed(1)}%)`}
                    metricType="success"
                    clickable={true}
                    isActive={filterConfig.activeFilter === "compliant"}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Data Grid - Hidden by default, shown when cards are clicked */}
        <Collapse in={filterConfig.showTable}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {filterConfig.getFilterDescription()} (
              {filterConfig.filteredData.length} deliveries)
            </Typography>
            <GaneshDataGrid
              loading={isLoading}
              data={filterConfig.filteredData}
              columns={columns_def}
              gridId="structure-checks-table"
              defaultGridState={defaultGridState}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                },
              }}
            />
          </Box>
        </Collapse>
      </Box>
    );
  }
};

export default StructureChecks;
